import { AfterViewInit, Component, DestroyRef, ElementRef, forwardRef, HostListener, inject, Input, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR, ReactiveFormsModule, Validator } from '@angular/forms';
import { AutocompleteOffInputDirective } from 'app/shared/form-utils/autocomplete-off.directive';
import { IsInvalidControlDirective } from 'app/shared/form-utils/is-invalid.directive';
import { NumberDirective } from 'app/shared/form-utils/number.validator';


@Component({
    selector: 'input-stepper',
    imports: [
        ReactiveFormsModule,
        AutocompleteOffInputDirective,
        IsInvalidControlDirective,
        NumberDirective,
    ],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => InputStepperComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => InputStepperComponent), multi: true },
    ],
    styles: [
        `
            :host {
                display: flex;
                flex-direction: row;
            }
            .icon-wrap {
                margin-left: 5px;
                flex-direction: column;
                gap:5px;
                align-content: center;
                display:flex;
            }
            .icon-wrap i {
                cursor: pointer;
                flex-basis: 50%;
            }
            .icon-wrap i:hover {
                color: #1067a8;
            }
        `
    ],
    template: `
        <div class="input-group">
            <ng-content></ng-content>
            <input
                #input
                type="ui-number"
                class="form-control"
                [formControl]="fc"
                [min]="min"
                [max]="max"
                [step]="step"
                [intOnly]="intOnly"
                (blur)="onTouched()">
        </div>
        <div class="icon-wrap">
            <i class="fa fa-chevron-up" tabindex="-1" (click)="increase()"></i>
            <i class="fa fa-chevron-down" tabindex="-1" (click)="decrease()"></i>
        </div>
    `,
})
export class InputStepperComponent implements ControlValueAccessor, AfterViewInit, Validator {

    private onChange = (_: any) => {};

    onTouched = () => {};

    @ViewChild('input') input: ElementRef<HTMLInputElement>;

    fc = new FormControl();

    @Input() step = 1;

    @Input() min: number;

    @Input() max: number;

    get intOnly() {
        return this.step === Math.round(this.step);
    }

    private destroyRef = inject(DestroyRef);

    ngAfterViewInit() {
        this.fc.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((v) => {
            this.onChange(v);
            this.onTouched();
        });
    }

    @HostListener('click')
    onClick() {
        this.input.nativeElement.focus();
    }

    increase() {
        this.fc.setValue(Math.min(this.fc.value + this.step, this.max));
    }

    decrease() {
        if (this.fc.value - this.step < this.min) {
            if (this.fc.value < this.min) {
                this.fc.setValue(this.min == 0 ? null : this.min);
            }
            return;
        }
        this.fc.setValue(this.fc.value - this.step);
    }

    validate(c: AbstractControl) {
        return this.fc.validator?.(c) || null;
    }

    writeValue(v: unknown) {
        this.fc.setValue(v, { emitEvent: false });
    }

    registerOnChange(fn: () => void) {
        this.onChange = fn;
    }

    registerOnTouched(fn: () => void) {
        this.onTouched = fn;
    }
}
